import { CategorySelector } from 'components/analytics/ExecutiveSummary/CategorySelector';
import { ComparisonTable } from 'components/analytics/ExecutiveSummary/ComparisonTable';
import { ExecutiveSummaryProvider } from 'components/analytics/ExecutiveSummary/ExecutiveSummaryContext';
import { TimeseriesChart } from 'components/analytics/ExecutiveSummary/TimeseriesChart';
import { BackToTop, ComposableFilters, DateSelector } from 'components/common';
import { SegmentSelector } from 'components/common/SegmentSelector';
import { ExportIcon, ExternalLinkIcon } from 'components/icons';
import { format } from 'date-fns';
import { isEmpty } from 'lodash';
import { lazy, Suspense, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'store';
import {
  clearApplied,
  composableFiltersState,
} from 'store/composableFiltersSlice';
import { userDetailsState } from 'store/userDetailsSlice';
import { usePageTitle } from 'utils';
import { useQuery } from 'utils/useQuery';

const ExportMenuLazy = lazy(
  () =>
    import('components/analytics/ExecutiveSummary/ExecutiveSummaryExportMenu')
);

export const ExecutiveSummary = () => {
  usePageTitle('Executive summary | Segments');

  const fullPageReportRef = useRef<HTMLDivElement>(null);

  const query = useQuery();

  const { currentStore } = useAppSelector(userDetailsState);
  const { output, selected } = useAppSelector(composableFiltersState);
  const dispatch = useAppDispatch();

  const storeName = currentStore?.completeInfo?.store_name?.replaceAll(
    ' ',
    '_'
  );
  const filename = `${storeName}-executive_summary-${format(
    Date.now(),
    'yyyyMMdd'
  )}`;

  const category = query.get('category') || 'overall';

  useEffect(() => {
    if (category !== 'overall' && !!!isEmpty(output.filtersApiPayload)) {
      dispatch(clearApplied());
    }
  }, [category, dispatch, output.filtersApiPayload, selected]);

  return (
    <ExecutiveSummaryProvider>
      <div className="max-w-screen-xl px-5 mx-auto md:px-14 py-11">
        <div ref={fullPageReportRef}>
          <p className="font-bold leading-6">Analytics</p>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h1 className="mt-4 font-bold leading-10 text-segments-44px">
                Executive summary
              </h1>
              <p className="mt-3 text-black">
                Monitor your business at a glance, with 14+ metrics such as AOV,
                ARPU, Repeat customers %, and Churn.
              </p>

              <a
                href="https://help.tresl.co/en/articles/5395981-executive-summary"
                className="inline-flex mt-2 font-bold text-segments-link items-start space-x-0.5 "
                target="_blank"
                rel="noreferrer"
              >
                <p>Learn more</p>
                <ExternalLinkIcon className="w-5 h-5" />
              </a>
            </div>
            <div className="flex justify-end">
              <Suspense
                fallback={
                  <div className="flex items-center p-3 rounded-lg bg-segments-gray focus:outline-none">
                    Export
                    <ExportIcon className="w-5 h-5" />
                  </div>
                }
              >
                <ExportMenuLazy
                  containerRef={fullPageReportRef}
                  filename={filename}
                >
                  <div className="flex items-center px-5 py-2.5 space-x-2 rounded-lg bg-segments-gray focus:outline-none">
                    <p>Export</p>
                    <ExportIcon className="w-5 h-5" />
                  </div>
                </ExportMenuLazy>
              </Suspense>
            </div>
          </div>
          <div className="relative flex items-center w-full space-x-4 mt-7">
            <div
              className={`w-full pl-5 pr-6 text-base align-middle border h-segments-60px bg-segments-accent-green-3 rounded-2xl  ${
                category !== 'overall' && 'opacity-50 pointer-events-none'
              }`}
            >
              <div className="flex items-center justify-between h-full align-middle">
                <SegmentSelector />
                <ComposableFilters />
              </div>
            </div>
            <div className="2xl:flex-shrink-0">
              <DateSelector defaultPeriod="Last 30 days" />
            </div>
          </div>
          <p className="mt-1.5 text-segments-13px  text-black text-opacity-50">
            Important: the following analysis only considers valid orders.{' '}
            <a
              href="https://help.tresl.co/en/articles/5432306-valid-orders"
              className="inline-flex items-start space-x-0.5 text-segments-link font-bold mt-2"
              target="_blank"
              rel="noreferrer"
            >
              Read more
              <ExternalLinkIcon className="inline ml-0.5 w-4 h-4 align-middle" />
            </a>
          </p>

          <CategorySelector />
          <TimeseriesChart />
          <ComparisonTable />

          <BackToTop />
        </div>
      </div>
    </ExecutiveSummaryProvider>
  );
};
